import { Suspense, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import Home from "./components/home";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyPage from "./pages/VerifyPage";
import { API_BASE } from "./lib/api";
import SignupPage from "./pages/SignupPage";
import LibraryPage from "./pages/LibraryPage";
import TranscriptViewPage from "./pages/TranscriptViewPage";
import TranscriptionPage from "./pages/TranscriptionPage";
import PricingPage from "./pages/PricingPage";
import BillingSuccessPage from "./pages/BillingSuccessPage";
import BillingCancelPage from "./pages/BillingCancelPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import AdminPage from "./pages/AdminPage";
import BrowsePage from "./pages/BrowsePage";
import PodcastEpisodesPage from "./pages/PodcastEpisodesPage";
import ChatPage from "./pages/ChatPage";
import SettingsPage from "./pages/SettingsPage";
import AccountPage from "./pages/AccountPage";
import HelpCenterPage from "./pages/HelpCenterPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import EchonourVsTurboscribe from "./pages/blog/EchonourVsTurboscribe";
import EchonourVsOtter from "./pages/blog/EchonourVsOtter";
import TranscriptsIndexPage from "./pages/TranscriptsIndexPage";
import CostCalculatorPage from "./pages/CostCalculatorPage";
import PodcastLandingPage from "./pages/PodcastLandingPage";
import Navbar from "./components/Navbar";
import LowBalanceBanner from "./components/LowBalanceBanner";
import Footer from "./components/Footer";
import TranscriptionBottomBar, {
  useBottomBarHeight,
} from "./components/TranscriptionBottomBar";
import { AuthProvider } from "./contexts/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import {
  TranscriptionProvider,
  useTranscriptionJobs,
} from "./contexts/TranscriptionContext";
import { getPaddle } from "./lib/paddle";

const AUTH_PATHS = ["/login", "/signup", "/forgot-password", "/reset-password", "/verify"];
const NO_FOOTER_PATHS = ["/chat", "/library"];

function AppContent() {
  const { pathname } = useLocation();
  const showNavbar = !AUTH_PATHS.includes(pathname);
  const showFooter = showNavbar && !NO_FOOTER_PATHS.includes(pathname);
  const showBottomBar = !NO_FOOTER_PATHS.includes(pathname);
  const { jobs } = useTranscriptionJobs();
  const spacerPx = useBottomBarHeight(jobs);

  // Apply dark mode from localStorage on mount
  useEffect(() => {
    if (localStorage.getItem("tempo_dark_mode") === "true") {
      document.documentElement.classList.add("dark");
    }
  }, []);

  // Prefetch Browse data in background so it's ready when user navigates
  useEffect(() => {
    fetch(`${API_BASE}/podcasts/latest-episodes?limit=10`).catch(() => {});
  }, []);

  // Handle _ptxn query param — Paddle email / payment-update redirects landing on default payment link
  useEffect(() => {
    const ptxn = new URLSearchParams(window.location.search).get("_ptxn");
    if (ptxn) {
      getPaddle().then((paddle) => {
        if (paddle) {
          paddle.Checkout.open({ transactionId: ptxn });
        }
      });
    }
  }, []);

  return (
    <div className="flex min-h-screen flex-col">
      {showNavbar && <Navbar />}
      {showNavbar && <LowBalanceBanner />}
      <main className="flex-1">
        <Suspense fallback={<p>Loading...</p>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify" element={<VerifyPage />} />
            <Route path="/library" element={<LibraryPage />} />
            <Route path="/transcript/:id" element={<TranscriptViewPage />} />
            <Route path="/transcription/:jobId?" element={<TranscriptionPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/billing/success" element={<BillingSuccessPage />} />
            <Route path="/billing/cancel" element={<BillingCancelPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/browse" element={<BrowsePage />} />
            <Route path="/browse/podcast" element={<PodcastEpisodesPage />} />
            <Route path="/help" element={<HelpCenterPage />} />
            <Route path="/faq" element={<HelpCenterPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/echonour-vs-turboscribe" element={<EchonourVsTurboscribe />} />
            <Route path="/blog/echonour-vs-otter" element={<EchonourVsOtter />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/transcripts" element={<TranscriptsIndexPage />} />
            <Route path="/transcripts/:slug" element={<PodcastLandingPage />} />
            <Route path="/tools/cost-calculator" element={<CostCalculatorPage />} />
          </Routes>
        </Suspense>
      </main>
      {showFooter && <Footer />}
      {showBottomBar && spacerPx > 0 && (
        <div style={{ height: `calc(${spacerPx}px + env(safe-area-inset-bottom))` }} />
      )}
      {showBottomBar && <TranscriptionBottomBar />}
    </div>
  );
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || "838413809715-s4ahik4rnsa8i13lpjfu0omcjka3djc7.apps.googleusercontent.com";

function App() {
  return (
    <ErrorBoundary>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <AuthProvider>
          <TranscriptionProvider>
            <AppContent />
          </TranscriptionProvider>
        </AuthProvider>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  );
}

export default App;
// rebuild 1772708825
